import React, { FC, PropsWithChildren, Suspense, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Widget } from '@funtech-int/rival';
import { useWidget as useRivalWidget, ErrorBoundary } from '@funtech-int/rival-react';

interface Options {
    strict?: boolean;
    lazyInView?: boolean;
    Fallback?: FC;
}

type WidgetRenderer<M, P> = FC<PropsWithChildren<P>> & {
    readonly enabled?: boolean;
    readonly meta?: M;
    readonly handlerEnabled?: boolean;
};

export function useWidget<M, P>(widget: Widget<M, P>, options: Options = {}): WidgetRenderer<M, P> {
    const handler = useRivalWidget(widget);

    return useMemo(() => {
        const Widget = (props: PropsWithChildren<P>) => {
            const [ref, isInView] = useInView({
                rootMargin: '100px',
                triggerOnce: true,
            });

            if (!handler.enabled) {
                return null;
            }

            const fallback = options.Fallback ? <options.Fallback /> : null;

            const content = (
                <ErrorBoundary fallback={() => null}>
                    <Suspense fallback={fallback}>
                        {/* eslint-disable-next-line react/destructuring-assignment */}
                        {handler.render(props as any, props.children)}
                    </Suspense>
                </ErrorBoundary>
            );

            if (options?.lazyInView) {
                return (
                    <div ref={ref} style={{ minWidth: 1 }}>
                        {isInView ? content : fallback}
                    </div>
                );
            }

            return content;
        };

        Widget.enabled = !!handler;
        Widget.handlerEnabled = handler.enabled;
        Widget.meta = handler.enabled ? handler?.meta : undefined;

        return Widget;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widget]);
}
